import { useState } from "react";
import { appConfig } from "@/config/appConfig";
import { getAuthToken } from "@/api/client";
import type { ListRequestsParams } from "@/api/endpoints";

function buildQueryString(params: ListRequestsParams): string {
  const search = new URLSearchParams();
  if (params.from) search.set("from", params.from);
  if (params.to) search.set("to", params.to);
  if (params.sort) search.set("sort", params.sort);
  if (params.dir) search.set("dir", params.dir);
  if (params.rework) search.set("rework", "true");
  if (params.q) search.set("q", params.q);
  const appendMulti = (key: string, values?: string[]) => {
    if (!values) return;
    for (const v of values) search.append(key, v);
  };
  appendMulti("qPills", params.qPills);
  appendMulti("requestType", params.requestType);
  appendMulti("status", params.status);
  appendMulti("calculatedStatus", params.calculatedStatus);
  appendMulti("client", params.client);
  appendMulti("lob", params.lob);
  appendMulti("eng", params.eng);
  appendMulti("assigned", params.assigned);
  appendMulti("country", params.country);
  appendMulti("bucket", params.bucket);
  return search.toString();
}

export interface ExportState {
  exporting: boolean;
  error?: string;
}

/**
 * Downloads /api/export/requests.csv with the current filter set as a Blob. Triggers a hidden
 * <a download> click so the browser saves the file. Returns state for the UI to show a spinner
 * / error inline.
 */
export function useExportRequests() {
  const [state, setState] = useState<ExportState>({ exporting: false });

  const exportNow = async (params: ListRequestsParams) => {
    setState({ exporting: true });
    try {
      const qs = buildQueryString(params);
      const url = `${appConfig.apiBaseUrl}/export/requests.csv${qs ? "?" + qs : ""}`;
      // Raw fetch (not apiFetch — we need the blob, not JSON), so attach the Bearer token
      // ourselves; the /api/export endpoint is behind the AnalyticsRead policy.
      const token = await getAuthToken();
      const headers: Record<string, string> = { Accept: "text/csv" };
      if (token) headers.Authorization = `Bearer ${token}`;
      const response = await fetch(url, { headers });
      if (!response.ok) {
        // 413 = row cap exceeded; surface the server's helpful message.
        let message = `Export failed (${response.status})`;
        try {
          const body = await response.json();
          if (typeof body?.error === "string") message = body.error;
        } catch {
          /* fall through */
        }
        setState({ exporting: false, error: message });
        return;
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = filenameFromHeader(response.headers.get("content-disposition")) ?? defaultFilename();
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Free the blob URL on the next tick to give the click time to start the download.
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      setState({ exporting: false });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Export failed";
      setState({ exporting: false, error: msg });
    }
  };

  return { ...state, exportNow };
}

function filenameFromHeader(header: string | null): string | null {
  if (!header) return null;
  const match = /filename="?([^";]+)"?/.exec(header);
  return match?.[1] ?? null;
}

function defaultFilename(): string {
  const d = new Date();
  const stamp = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;
  return `requests-${stamp}.csv`;
}
